import React, { useRef, useState, useEffect } from "react";
import styles from "./ClientInfoTab.module.css";
import i18n from "../../Services/i18n";
import { useToasts } from "../../LayoutComponents/ToastContext";
import ImagePreview from "../ImagePreview/ImagePreview";
import DateTimeService from "../../Services/datetimeService";
import TagsInput from "../TagsInput/TagsInput";
import FileUploaderModal from "../FileUploaderModal/FileUploaderModal";
import FileService from "../../Services/fileService";
import ClientService from "../../Services/clientService";
import { useClients } from "../../Context/ClientsContext";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import { useModal } from "../../Context/ModalContext";
import FileFolderPreview from "../FileFolderPreview/FileFolderPreview";
import PrivilegeValidationService from "../../Services/privilegeValidationService";
import { usePrivileges } from "../../Context/PrivilegeContext";
import RichEditor from "../RichEditor/RichEditor";
import RichEditorHelper from "../../Tools/RichEditorHelper";
import { useDeletionConfirmationModal } from "../../Hooks/DeletionConfirmationModal";
import clientIcon from "../../Assets/Icons/client_black.png";

const ClientInfoTab = ({ client, reloadDetailClient, refreshClientList, lockParent }) => {
  const fileUploadModal = useRef(null);

  const [firstName, setFirstName] = useState(client.firstName || "");
  const [lastName, setLastName] = useState(client.lastName || "");
  const [companyName, setCompanyName] = useState(client.companyName || "");
  const [phone, setPhone] = useState(client.phone || "");
  const [address, setAddress] = useState(client.address || "");
  const [email, setEmail] = useState(client.email || "");
  const [description, setDescription] = useState(client.description || "");
  const [defaultImage, setDefaultImage] = useState(client.defaultImage);
  const [defaultImageId, setDefaultImageId] = useState(client.defaultImageId);
  const [tags, setTags] = useState(client.tags || "");
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canArchive, setCanArchive] = useState(false);
  const descEditorRef = useRef(null);
  const [editorResetKey, setEditorResetKey] = useState(0);
  const defaultImageFromMediaIdRef = useRef(null);
  const { updateClient } = useClients();
  const { openModal } = useModal();
  const { addToast } = useToasts();
  const { privileges } = usePrivileges();
  const confirmDelete = useDeletionConfirmationModal();

  useEffect(() => {
    const loadArchivePrivilege = async () => {
      setCanArchive(PrivilegeValidationService.CanArchive(await privileges()));
    };

    loadArchivePrivilege();
  }, []);

  const handleChange = (setter) => (event) => {
    setter(event.target.value);
    setIsModified(true);
    lockParent(true);
  };
  const isArchived = client.isArchived === true;
  const handleDelete = () => {
    const clientTitle =
      client.cardName ||
      [client.firstName, client.lastName].filter(Boolean).join(" ") ||
      client.companyName ||
      "";

    confirmDelete({
      entityId: client.externalId,
      entityTitle: clientTitle,
      entitySubTitle: null,
      icon: clientIcon,
      entityImage: client.defaultImage,

      deleteFunction: (entityId) => ClientService.DeleteClient(entityId),

      onClose: async (refreshNeeded) => {
        if (!refreshNeeded) return;

        lockParent(false);

        if (typeof refreshClientList === "function") {
          await refreshClientList();
        }
      },
    });
  };

  const handleArchiveClick = async () => {
    if (!isArchived && !canArchive) {
      addToast(i18n.t("Generic.PremiumFeatureOnly"), "ARCHIVE_PREMIUM_REQUIRED", "INFO");
      return;
    }

    try {
      setIsSaving(true);

      const result = isArchived
        ? await ClientService.UnarchiveClient(client.externalId)
        : await ClientService.ArchiveClient(client.externalId);

      if (result) {
        lockParent(false);
        setIsModified(false);

        await refreshClientList(client.externalId);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeValue = (setter) => (value) => {
    setter(value);
    setIsModified(true);
    lockParent(true);
  };

  const handleDefaultImageButtonClick = async () => {
    if (PrivilegeValidationService.ClientCanAddMedia(await privileges(), client.medias.length)) {
      fileUploadModal.current.open();
    } else {
      openModal({
        title: i18n.t("Generic.DefaultImageOptions.SelectFromMedias"),
        hideSaveButton: true,
        form: "formHorse",
        content: (
          <>
            {" "}
            <div style={{ margin: "0 10px" }}>
              <p>{i18n.t("Generic.DefaultImageOptions.MustSelectFromMedias")}</p>
            </div>
            <FileFolderPreview
              refresh={() => {}}
              files={client.medias}
              viewMode={"icon"}
              onFileSelected={(fileId) => {
                defaultImageFromMediaIdRef.current = fileId;
              }}
              smallIcon={false}
              canDelete={false}
              hideHeaderMenu={true}
            />
          </>
        ),
        onSave: async () => {
          try {
            const fileId = defaultImageFromMediaIdRef.current;
            if (!fileId) return;
            const img = await FileService.getFile(fileId);
            setDefaultImageId(fileId);
            setDefaultImage(img);
            setIsModified(true);
            lockParent(true);
            return true;
          } catch (ex) {
            addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
            return false;
          }
        },
        onClose: () => {},
      });
    }
  };

  const handleSave = async () => {
    const isValid = validate();
    if (!isValid) return;

    setIsSaving(true);
    const updatedClientInfo = {
      externalId: client.externalId,
      firstName,
      lastName,
      email,
      address,
      companyName,
      phone,
      description,
      tags,
      defaultImageId,
    };
    const result = await ClientService.Update(updatedClientInfo);
    if (result) {
      reloadDetailClient(client);
      updateClient(client.externalId);
      lockParent(false);
      setIsModified(false);
    }
    setIsSaving(false);
  };

  const validate = () => {
    const EMAIL_RGX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (firstName === "" && lastName === "" && companyName === "") {
      addToast(i18n.t("ClientForm.Validation.NameRequired"), "clientFormValidationName", "ERROR");
      return false;
    }

    if (email !== "" && !EMAIL_RGX.test(email)) {
      addToast(i18n.t("ClientForm.Validation.InvalidEmail"), "clientFormValidationEmail", "ERROR");
      return false;
    }

    if (description.length > 40000) {
      addToast(
        i18n.t("ClientForm.Validation.InvalidDescriptionLen"),
        "clientFormValidationEmail",
        "ERROR",
      );
      return false;
    }

    return true;
  };

  const handleReset = () => {
    setFirstName(client.firstName || "");
    setLastName(client.lastName || "");
    setCompanyName(client.companyName || "");
    setPhone(client.phone || "");
    setAddress(client.address || "");
    setEmail(client.email || "");
    setTags(client.tags || "");
    setDefaultImageId(client.defaultImageId);
    setDefaultImage(client.defaultImage);
    setIsModified(false);
    lockParent(false);

    const initialHtml = client.description || "";
    setDescription(initialHtml);
    descEditorRef.current?.setHTML(initialHtml);
    setEditorResetKey((k) => k + 1);
  };

  return (
    <div className={styles.container}>
      <div
        className={`${styles.content} ${isArchived ? styles.archivedReadOnly : ""}`}
        inert={isArchived}>
        <div className={styles.leftSection}>
          <div className={styles.inlineRow}>
            <div className={styles.leftSectionLabelKey}>
              <label>{i18n.t("ClientForm.Firstname")}:</label>
              <input
                type="text"
                maxLength={200}
                className={styles.inputMedium}
                value={firstName}
                onChange={handleChange(setFirstName)}
              />
            </div>
            <div className={styles.leftSectionLabelKey}>
              <label>{i18n.t("ClientForm.Lastname")}:</label>
              <input
                type="text"
                maxLength={200}
                className={styles.inputMedium}
                value={lastName}
                onChange={handleChange(setLastName)}
              />
            </div>
          </div>

          <div className={styles.inlineRow}>
            <div className={styles.leftSectionLabelKey}>
              <label>{i18n.t("ClientQuickForm.FieldPhone")}</label>
              <input
                type="text"
                className={styles.inputSmall}
                value={phone}
                maxLength={45}
                onChange={handleChange(setPhone)}
              />
            </div>
            <div className={styles.leftSectionLabelKey}>
              <label>{i18n.t("ClientForm.Company")}:</label>
              <input
                type="text"
                className={styles.inputLarge}
                value={companyName}
                maxLength={200}
                onChange={handleChange(setCompanyName)}
              />
            </div>
          </div>

          <div className={styles.inlineRow}>
            <div className={styles.leftSectionLabelKey}>
              <label>{i18n.t("ClientForm.Email")}:</label>
              <input
                type="text"
                className={styles.inputLarge}
                value={email}
                maxLength={1000}
                onChange={handleChange(setEmail)}
              />
            </div>
            <div className={styles.leftSectionLabelKey}>
              <label>{i18n.t("ClientForm.Address")}:</label>
              <input
                type="text"
                className={styles.inputLarge}
                value={address}
                maxLength={400}
                onChange={handleChange(setAddress)}
              />
            </div>
          </div>

          <div>
            <label className={styles.descriptionSection}>{i18n.t("ClientForm.Description")}:</label>
            <RichEditor
              ref={descEditorRef}
              key={`client-desc-${client.externalId}-${editorResetKey}`}
              controlled
              value={description}
              onChange={(html) => {
                setDescription(html);

                const left = RichEditorHelper.normalizeHtmlForCompare(html);
                const right = RichEditorHelper.normalizeHtmlForCompare(client.description);

                if (left !== right) {
                  setIsModified(true);
                  lockParent(true);
                }
              }}
              placeholder={""}
              height={300}
              showEmoji
              showTextColor
              showFontSize
              showBold
              showItalic
              showUnderline
              showStrikethrough
            />
          </div>

          <div className={styles.tagsSection}>
            <label>{i18n.t("ClientForm.Tags")}:</label>
            <TagsInput value={tags ?? ""} onChange={handleChangeValue(setTags)} />
          </div>
        </div>

        {/* Right */}
        <div className={styles.rightSection}>
          <div className={styles.metaInfo}>
            <label>
              {i18n.t("ClientForm.CreationDate")}:{" "}
              {DateTimeService.formatDateString(client.createdOn, true)}
            </label>
            {client.modifiedOn && (
              <label>
                {i18n.t("ClientForm.UpdateDate")}:{" "}
                {DateTimeService.formatDateString(client.modifiedOn, true)}
              </label>
            )}
          </div>
          <div className={styles.imgPreview}>
            <ImagePreview imageUrl={defaultImage} />
            <FileUploaderModal
              ref={fileUploadModal}
              showMultiImageTab={false}
              callback={async (thumbnail, fileId) => {
                const img = await FileService.getFile(fileId);
                setDefaultImage(img);
                setDefaultImageId(fileId);
                setIsModified(true);
                lockParent(true);
                fileUploadModal.current.close();
              }}
              allowMultipleFiles={false}
            />
            <div className={styles.buttonUpdateDefaultPictureContainer}>
              <button
                className={styles.buttonUpdateDefaultPicture}
                onClick={handleDefaultImageButtonClick}>
                {i18n.t("PictureUploadField.LoadAnImage")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <button
            className={styles.formDeleteButton}
            disabled={isSaving || isArchived}
            onClick={handleDelete}>
            {i18n.t("Generic.Delete")}
          </button>

          <div
            className={styles.archiveButtonWrapper}
            title={!canArchive ? i18n.t("Generic.PremiumFeatureOnly") : ""}>
            <button
              className={`${styles.formArchiveButton} ${
                isArchived ? styles.formUnarchiveButton : ""
              } ${!isArchived && !canArchive ? styles.formArchiveButtonDisabled : ""}`}
              disabled={isSaving}
              aria-disabled={!isArchived && !canArchive}
              onClick={handleArchiveClick}>
              {isArchived ? i18n.t("Generic.Unarchive") : i18n.t("Generic.Archive")}
            </button>
          </div>
        </div>

        <div className={`${styles.footerRight} ${isArchived ? styles.archivedFooterActions : ""}`}>
          <button
            className={styles.formSaveButton}
            disabled={isArchived || !isModified}
            onClick={handleSave}>
            {i18n.t("Generic.Save")}
          </button>

          <button
            className={styles.formResetButton}
            disabled={isArchived || !isModified}
            onClick={handleReset}>
            {i18n.t("Generic.ResetForm")}
          </button>
        </div>
      </div>
      {isSaving && (
        <div className={styles.savingOverlay}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default ClientInfoTab;
